// ============================================================
// Chess Arena — Tool Manager
// ============================================================
// Manages in-match Tool state (Reroll, Instant Spawn).
// Tools cost gold per use and have per-match use limits.
// ============================================================

import { ToolConfigEntry } from '../core/types';
import eventBus from '../core/eventBus';
import { playerState } from '../core/playerState';
import gameConfig from '../core/gameConfig';

export class ToolManager {
  private toolConfigs: Record<string, ToolConfigEntry> = {};
  private usesRemaining: Record<string, number> = {};

  init(): void {
    const cfg = gameConfig.getToolConfig();
    if (!cfg) return;

    this.toolConfigs = cfg.tools;

    // Initialize per-match use counters
    for (const [id, tool] of Object.entries(this.toolConfigs)) {
      this.usesRemaining[id] = tool.usesPerMatch;
    }
  }

  /**
   * Attempt to use a tool. Returns true if successful.
   * Checks: unlocked, uses remaining, gold available.
   */
  useTool(toolId: string): boolean {
    const tool = this.toolConfigs[toolId];
    if (!tool) return false;

    // Check unlock
    if (playerState.highestLevelCompleted < tool.unlocksAtLevel - 1) return false;

    // Check uses remaining
    if ((this.usesRemaining[toolId] ?? 0) <= 0) return false;

    // Check and deduct gold
    if (!playerState.spendCoins(tool.goldCost)) return false;

    // Deduct use
    this.usesRemaining[toolId]--;

    // Emit event for BattleScene to handle
    eventBus.emit('tool_used', { tool: toolId });

    return true;
  }

  getUsesRemaining(toolId: string): number {
    return this.usesRemaining[toolId] ?? 0;
  }

  isToolUnlocked(toolId: string): boolean {
    const tool = this.toolConfigs[toolId];
    if (!tool) return false;
    return playerState.highestLevelCompleted >= tool.unlocksAtLevel - 1;
  }

  isToolAvailable(toolId: string): boolean {
    const tool = this.toolConfigs[toolId];
    if (!tool) return false;
    if (!this.isToolUnlocked(toolId)) return false;
    if ((this.usesRemaining[toolId] ?? 0) <= 0) return false;
    if (playerState.coins < tool.goldCost) return false;
    return true;
  }

  getToolConfig(toolId: string): ToolConfigEntry | undefined {
    return this.toolConfigs[toolId];
  }

  getAllTools(): Record<string, ToolConfigEntry> {
    return this.toolConfigs;
  }

  destroy(): void {
    this.toolConfigs = {};
    this.usesRemaining = {};
  }
}
